import React, { Component } from "react";
import "./Negotiations.scss";
import Axios from "axios-https-proxy-fix";
import md5 from "md5";
// -- Dialog Dependencies --
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import PrintNegotiations from "./PrintNegotiations";
import { connect } from "react-redux";
import * as actionTypes from "../../../store/actions";
import Translated from "../../../components/Translated/Translated";
import translator, {translate_switch} from "../../../utils/translator";
import checkSettings from "../../../utils/checkSettings";

 class Negotiations extends Component {
	/*
    props structure:
    {
        customer_name,
        project_title,
        consultant_name,
        warehouse,
        ship_to,
        products: {
            [id]: {
                sku,
                model,
                quantity,
                en_description,
                fr_description,
                list,
                standard_cost,
                unit_net,
                extended_net
            }
        },
        rooms : {
            [id]: {
                title,
                products: [
                    id
                ]
            }
        }
    }
    */

    constructor(props) {
        super(props);
        this.state = {
            total: 0,
            products: {},
            manual_prices: {},
            changed: {},
            md5_hashed_project_ID: null, // This is used for redirecting the user to the correct print page. 
            dialog: null,
            excelLoading: false,
            pdfLoading: false,
        };
    }

    componentDidMount = async () => {
        // ---- This is used for redirecting the user to the correct print page. ----
        let md5_hashed_project_ID = md5(this.props.project_ID.toString());
        this.setState({ md5_hashed_project_ID })

        // this makes the quote link on the nav bar to show consistently 
        setTimeout(async () => {
            await this.props.display_quote_link_on_nav_bar(true);
		}, 2000);
    }

    componentDidUpdate = (prevProps) => {
        if (!prevProps.updated && this.props.updated) {
            let newState = this.calculateState(this.props);

            // Stores flags for each product of whether it's been edited
            newState.changed = this.objectMap(this.props.products, () => false);

            this.setState(newState);

            if(!!this.props.notInWarehouseProduct) {
                this.showNotInWarehouseDialog();
            }
        }
    };

    calculateState = (props) => {
        let parsedProducts = this.objectMap(props.products, item => {
            return this.calculateProduct(item, item.extended_net);
        });
        return {
            total: this.calculateTotal(parsedProducts),
            products: parsedProducts
        };
    }

    objectMap = (inputObject, func) => {
        return Object.fromEntries(
            Object.entries(inputObject).map(keyval => {
                return [keyval[0], func(keyval[1])];
            })
        );
    };

    calculateTotal = products => {

        let rooms = this.props.rooms;
        rooms = Object.values(rooms);
        rooms = rooms.map(room => Object.entries(room.products));
        rooms = rooms.reduce((arr, room) => { return arr.concat(room) }, []);

        return rooms.reduce((total, [id, quantity]) => {
            return total + quantity * products[id].extended_net;
        }, 0.00).toFixed(2)
    };

    calculateProduct = (item, new_net) => {
        return ["discount_dollar", "discount_percentage", "margin_dollar", "margin_percentage", "extended_net"]
            .map(key => [key, parseFloat(this.calculateDerived(item, key, new_net)).toFixed(2)])
            .reduce((obj, arr) => {
                obj[arr[0]] = arr[1];
                return obj;
            }, {
                list: item.list,
                standard_cost: item.standard_cost,
                hidden_net: new_net,
                disable_save: item.hasOwnProperty("disable_save") ? item.disable_save : true,
                disable_reset: item.hasOwnProperty("disable_reset") ? item.disable_reset : true
            });
    };

    calculateDerived = (item, key, new_net) => {
        switch (key) {
            case "discount_dollar":
                return item.list - new_net;
            case "discount_percentage":
                return !parseFloat(item.list) ? 0.0 : (100.0 * (item.list - new_net)) / item.list;
            case "margin_dollar":
                return new_net - item.standard_cost;
            case "margin_percentage":
                return !parseFloat(new_net) ? 0.0 : (100.0 * (new_net - item.standard_cost)) / new_net;
            case "standard_cost":
                return item.standard_cost === "0.00" ? item.standard_cost : new_net;
            default:
                return new_net;
        }
    };

    calculateExtendedNet = (item, key, new_val) => {
        switch (key) {
            case "discount_dollar":
                return item.list - new_val;
            case "discount_percentage":
                return item.list - (new_val * item.list) / 100.0;
            case "margin_dollar":
                return parseFloat(item.standard_cost) + parseFloat(new_val);
            case "margin_percentage":
                return !parseFloat(100.0 - new_val) ? 0.0 : (100.0 * item.standard_cost) / (100.0 - new_val);
            default:
                return new_val;
        }
    };

    changeValue = (id, key, val) => {
        let state = this.state;
        let parsedVal = parseFloat(val) ? parseFloat(val) : 0.0;
        let newProducts = {
            ...state.products,
            [id]: {
                ...this.calculateProduct(state.products[id], this.calculateExtendedNet(state.products[id], key, parsedVal)),
                [key]: val,
                disable_save: false
            }
        };
        this.setState(state => ({
            products: newProducts,
            total: this.calculateTotal(newProducts),
            changed: {
                ...state.changed,
                [id]: true
            }
        }));
    };

    fixValue = (id, key) => {
        this.setState(state => ({
            products: {
                ...state.products,
                [id]: {
                    ...state.products[id],
                    [key]: parseFloat(this.calculateDerived(state.products[id], key, state.products[id].hidden_net)).toFixed(2)
                }
            }
        }));
    };

    recalcValue = (id, key) => {
        this.setState(state => ({
            products: {
                ...state.products,
                [id]: {
                    ...state.products[id],
                    [key]: this.calculateDerived(state.products[id], key, state.products[id].hidden_net)
                }
            }
        }));
    };

    callSave = async (id) => {
        let product = this.state.products[id];
        let product_info = this.props.products[id];
        let APIUrl = this.props.APIUrl + "/projects/update-negotiations";
        
        let data = {
            id: id,
            room_product_id: product_info.room_product_id,
            stocked: product_info.stocked,
            extended_net: product.extended_net,
            list: product.list,
            discount_percentage: this.calculateDerived(product, "discount_percentage", product.extended_net),
            margin_percentage: this.calculateDerived(product, "margin_percentage", product.extended_net),
            standard_cost: product.standard_cost
        };
        await Axios.post(APIUrl, data);
        return 1;
    }

    saveProduct = async (id) => {
        // save()
        await this.callSave(id);
        this.setState(state => ({
            products: {
                ...state.products,
                [id]: {
                    ...state.products[id],
                    disable_save: true,
                    disable_reset: false
                }
            }
        }));
    };

    resetProduct = async id => {
        // save()
        await this.setState(state => ({
            products: {
                ...state.products,
                [id]: this.calculateProduct(this.props.products[id], this.props.products[id].unit_net)
            },
            changed: {
                ...state.changed,
                [id]: true
            }
        }));
        
        await this.callSave(id);

        this.setState(state => ({
            total: this.calculateTotal(state.products),
            products: {
                ...state.products,
                [id]: {
                    ...state.products[id],
                    disable_save: true,
                    disable_reset: true,
                }
            }
        }));
    };

    buildInput = (id, key, room_id) => {
        let val = this.state.changed[id] ? this.state.products[id][key] : parseFloat(this.calculateDerived(this.state.products[id], key, this.props.rooms[room_id].manual_prices[id])).toFixed(2)
        return (
            <input
                type="number"
                class="negotiate-value-amount"
                value={val}
                onChange={e => {
                    this.changeValue(id, key, e.target.value);
                }}
                onBlur={e => {
                    this.fixValue(id, key, e.target.value);
                }}
                onFocus={e => {
                    this.recalcValue(id, key);
                }}
            />
        );
    };

    export_negotiations = async (type) => {
        let export_negotiations_to_excel_API_URL = this.props.APIUrl + "/negotiations/export"
        let data = {
            project_id: this.props.project_ID,
            type: type,
            trendId: localStorage.getItem("trendId"),
            token: localStorage.getItem("token"),
        }

        // The reason I didn't use the response message because it's throwing an error in the php code even though it works. 
        // Didn't bother because felt like it wasn't worth spending the time for when it already works.
        let export_negotiations_to_excel_API_response = await Axios.post(export_negotiations_to_excel_API_URL, data);

        let dialog_content = <Dialog open={true} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title set-price-dialog-header">
                {translator("Misc", "success_label")}{" "}
            </DialogTitle>
            <DialogContent >
                <DialogContentText >
                    {translator("Negotiations", "negotiations_export_success")}
                </DialogContentText>
                <Button id="set-price-close-button" onClick={() => this.setState({ dialog: null })} color="primary">
                    <Translated section="Misc" label="ok_btn" />
				</Button>
            </DialogContent>
        </Dialog>

        switch (type) {
            case 'excel':
                this.setState({ excelLoading: false });
                break;
            case 'pdf':
                this.setState({ pdfLoading: false });
                break;
            default:
                break;
        }

        this.setState({ dialog: dialog_content })
    }

    showNotInWarehouseDialog = async () => {
        let dialog_content = <Dialog open={true} aria-labelledby="form-dialog-title">
            <DialogContent>
                <DialogContentText class="transfer-order-notice">
                    <Translated section="Misc" label="not_in_warehouse_item_negotiations_notice" />
                </DialogContentText>
                <Button 
                    id="set-price-close-button" 
                    onClick={() => this.setState({ dialog: null })} 
                    color="primary"
                >
                    <Translated section="Misc" label="ok_btn" />
                </Button>
            </DialogContent>
        </Dialog>
        this.setState({ dialog: dialog_content });
    };

    render() {
        return (
            <div>
                {this.state.dialog}
                <nav class="navbar navbar-light light-blue lighten-4 project-quote-collapse-header must-be-relative">
                    <h1 id="settings-header"><Translated section="Negotiations" label="page_title" /></h1>
                    <button class="navbar-toggler toggler-example quote-whole-nav-toggle" type="button" data-toggle="collapse" data-target="#navbarSupportedContent-project-quote" aria-controls="navbarSupportedContent-project-quote" aria-expanded="false" aria-label="Toggle navigation">
                        <span class="dark-blue-text project-quote-hamburger-icon">
                            <i class="fas fa-bars fa-1x" />
                        </span>
                    </button>

                    {/* Drop Down Content */}
                    <div class="collapse navbar-collapse" id="navbarSupportedContent-project-quote">
                        <ul class="navbar-nav mr-auto project-quote-list">
                            <li class="nav-item">
                                <PrintNegotiations></PrintNegotiations>
                            </li>
                            <li class="nav-item">
                                <div class="quote-nav-component">
                                    <button onClick={() => {
                                        this.setState({ excelLoading: true });
                                        this.export_negotiations("excel");
                                    }} 
                                    disabled={this.state.excelLoading ? true : false}
                                    class="quote-nav-top-link">
                                        <div></div>
                                        {this.state.excelLoading
                                            ? <i class="fa fa-spinner fa-spin"></i>
                                            : <div><Translated section="Misc" label="export_excel" /></div>
                                        }
                                    </button>
                                </div>
                            </li>
                            <li class="nav-item">
                                <div class="quote-nav-component">
                                    <button onClick={() => {
                                        this.setState({ pdfLoading: true });
                                        this.export_negotiations("pdf")
                                    }} 
                                    disabled={this.state.pdfLoading ? true : false}
                                    class="quote-nav-top-link">
                                        <div></div>
                                        {this.state.pdfLoading
                                            ? <i class="fa fa-spinner fa-spin"></i>
                                            : <div><Translated section="Misc" label="export_pdf" /></div>
                                        }
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </div>

                </nav>

                <p class="settings-sub-header">
                    <br />
                </p>

                <div className="negotiations-wrapper">
                    <div class="negotiate-header">
                        <div class="negotiate-header-box">
                            <div class="negotiate-header-title"><Translated section="Negotiations" label="customer_label" /></div>
                            <div class="negotiate-header-value">{this.props.customer_name}</div>
                        </div>
                        <div class="negotiate-header-box">
                            <div class="negotiate-header-title"><Translated section="Misc" label="project_title_label" /></div>
                            <div class="negotiate-header-value">{this.props.project_title}</div>
                        </div>
                        <div class="negotiate-header-box">
                            <div class="negotiate-header-title"><Translated section="Negotiations" label="prepared_by_label" /></div>
                            <div class="negotiate-header-value">{this.props.consultant_name}</div>
                        </div>
                        <div class="negotiate-header-box">
                            <div class="negotiate-header-title"><Translated section="Negotiations" label="warehouse_label" /></div>
                            <div class="negotiate-header-value">{this.props.warehouse}</div>
                        </div>
                        <div class="negotiate-header-box">
                            <div class="negotiate-header-title"><Translated section="Negotiations" label="ship_to_label" /></div>
                            <div class="negotiate-header-value">{this.props.ship_to}</div>
                        </div>
                        <div class="negotiate-header-box">
                            <div class="negotiate-header-title"><Translated section="Negotiations" label="quote_total_label" /></div>
                            <div class="negotiate-header-value negotiate-total">${this.state.total}</div>
                        </div>
                    </div>

                    <table class="negotiate-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th><Translated section="Negotiations" label="sku_label" /></th>
                                {!checkSettings("hide_model_number") && (
                                    <th><Translated section="Negotiations" label="model_label" /></th>
                                )}
                                <th><Translated section="Negotiations" label="qty_label" /></th>
                                <th><Translated section="Negotiations" label="desc_label" /></th>
                                <th><Translated section="Negotiations" label="list_label" /></th>
                                <th><Translated section="Negotiations" label="standard_label" /></th>
                                <th><Translated section="Negotiations" label="unit_net_label" /></th>
                                <th><Translated section="Negotiations" label="discount_dollar_label" /></th>
                                <th><Translated section="Negotiations" label="discount_percentage_label" /></th>
                                <th><Translated section="Negotiations" label="margin_dollar_label" /></th>
                                <th><Translated section="Negotiations" label="margin_percentage_label" /></th>
                                <th><Translated section="Negotiations" label="extended_net_label" /></th>
                                <th></th>
                            </tr>
                        </thead>

                        {this.props.order.map((r, i) => {
                            let room_arr = [r.room, this.props.rooms[r.room]];
                            let room = room_arr[1];
                            if (Object.keys(this.state.products).length === 0 && this.state.products.constructor === Object) {
                                // If no products loaded, do not attempt to calculate the table
                                return <tbody key={i}></tbody>;
                            }
                            return (
                                <tbody key={i}>
                                    <tr class="negotiate-room-header">
                                        <td rowSpan={r.products.length + 1}>
                                            {room.title}
                                        </td>
                                    </tr>

                                    {r.products.map(p => {
                                        let arr = [p, room.products[p]];
                                        let [id, quantity] = arr;
                                        let item = this.props.products[id];
                                        return (
                                            <tr key={id} className={item.stocked ? "" : "non-stock"}>
                                                <td class="negotiate-sku">
                                                    {item.stocked 
                                                    ? item.sku
                                                    : <span class="non-stock-badge">NS</span>}
                                                    {item.from_catalog && <span class="not-in-warehouse-badge">NW</span>}                         
                                                </td>
                                                {!checkSettings("hide_model_number") && (
                                                    <td class="negotiate-model">{translate_switch(item.model)}</td>
                                                )}
                                                <td class="negotiate-quantity">{quantity}</td>
                                                <td class="negotiate-description">
                                                    {translate_switch({
                                                        en: item.en_description,
                                                        fr: item.fr_description
                                                    })}
                                                </td>
                                                <td class="negotiate-list negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">$</div>
                                                        <div class="negotiate-value-amount">{item.list}</div>
                                                    </div>
                                                </td>
                                                <td class="negotiate-standard-cost negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">$</div>
                                                        {
                                                            !!item.from_catalog 
                                                            ? this.buildInput(id, "standard_cost", room_arr[0])
                                                            : <div class="negotiate-value-amount">{item.standard_cost}</div>
                                                        }
                                                    </div>
                                                </td>
                                                <td class="negotiate-unit-net negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">$</div>
                                                        <div class="negotiate-value-amount">{item.unit_net}</div>
                                                    </div>
                                                </td>
                                                <td class="negotiate-discount-dollar negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">$</div>
                                                        {item.stocked
                                                            ? this.buildInput(id, "discount_dollar", room_arr[0])
                                                            : <div class="negotiate-value-amount">0.00</div>
                                                        }
                                                    </div>
                                                </td>
                                                <td class="negotiate-discount-percentage negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">%</div>
                                                        {item.stocked
                                                            ? this.buildInput(id, "discount_percentage", room_arr[0])
                                                            : <div class="negotiate-value-amount">0.00</div>
                                                        }
                                                    </div>
                                                </td>
                                                <td class="negotiate-margin-dollar negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">$</div>
                                                        {item.stocked
                                                            ? this.buildInput(id, "margin_dollar", room_arr[0])
                                                            : <div class="negotiate-value-amount">0.00</div>
                                                        }
                                                    </div>
                                                </td>
                                                <td class="negotiate-margin-percentage negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">%</div>
                                                        {item.stocked
                                                            ? this.buildInput(id, "margin_percentage", room_arr[0])
                                                            : <div class="negotiate-value-amount">0.00</div>
                                                        }
                                                    </div>
                                                </td>
                                                <td class="negotiate-extended-net negotiate-value">
                                                    <div class="negotiate-value-flex">
                                                        <div class="negotiate-value-unit">$</div>
                                                        {this.buildInput(id, "extended_net", room_arr[0])}
                                                    </div>
                                                </td>
                                                <td class="negotiate-buttons">
                                                    <button
                                                        disabled={this.state.products[id].disable_save}
                                                        onClick={e => { this.saveProduct(id) }}
                                                    ><span role="img" aria-label="save">&#10003;</span></button>
                                                    <button
                                                        disabled={this.state.products[id].disable_reset}
                                                        onClick={e => {
                                                            this.resetProduct(id);
                                                        }}
                                                    ><span role="img" aria-label="reset">&#10008;</span></button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr><td class="negotiate-spacer" colspan="14"></td></tr>
                                </tbody>
                            );
                        })}
                    </table>
                    <div>
                        <button
                            class="negotiate-reset-all"
                            onClick={async () => {
                                await Promise.all(Object.entries(this.state.products).map(async ([id, item]) => {
                                    if(!this.props.products[id].stocked) {
                                    }
                                    else if(!item.disable_reset || !item.disable_save) {
                                        await this.resetProduct(id);
                                    }
                                    return;
                                }));
                            }}
                        ><Translated section="Negotiations" label="reset_btn" /></button>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    APIUrl: state.APIUrl,
	selected_language: state.selected_language // Added so that the component reloads when the language is changed
});
const mapDispatchToProps = dispatch => {
	return {
		display_quote_link_on_nav_bar: displayOrNot =>
			dispatch({
				type: actionTypes.DISPLAY_QUOTE_LINK_ON_NAV_BAR,
				displayOrNot
			}),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(Negotiations);